import { useState, useEffect } from 'react';
import {
  getTeamMembers,
  inviteUser,
  updateMemberRole,
  removeMember,
} from '../lib/supabase';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: '#c03a1a', bg: '#fff0ee' },
  auditor:     { label: 'Auditor',     color: '#6040a0', bg: '#f0e8ff' },
  admin:       { label: 'Admin',       color: '#c98a00', bg: '#fff8e6' },
  manager:     { label: 'Manager',     color: '#1a5080', bg: '#e0f0f8' },
  member:      { label: 'Member',      color: '#1a5c3a', bg: '#e4f2eb' },
};

export default function TeamPage({ session, profile, onBack }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const teamId = profile?.team_id;
  const teamName = profile?.teams?.name;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const currentUserId = session?.user?.id;

  useEffect(() => {
    if (teamId) loadMembers();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMembers = async () => {
    setLoading(true);
    const { data } = await getTeamMembers(teamId);
    setMembers(data || []);
    setLoading(false);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError('');
    setSuccess('');

    const { error } = await inviteUser(
      inviteEmail.trim(),
      invitePassword,
      inviteName.trim(),
      teamId,
      inviteRole
    );

    setInviting(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess(`${inviteName} has been added to the team.`);
      setShowInvite(false);
      setInviteEmail('');
      setInviteName('');
      setInvitePassword('');
      setInviteRole('member');
      loadMembers();
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    if (memberId === currentUserId) return; // can't change own role
    await updateMemberRole(memberId, newRole);
    setMembers(prev =>
      prev.map(m => m.id === memberId ? { ...m, role: newRole } : m)
    );
  };

  const handleRemove = async (memberId, memberName) => {
    if (memberId === currentUserId) return;
    if (!window.confirm(`Remove ${memberName} from the team?`)) return;
    await removeMember(memberId);
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    const bytes = new Uint32Array(12);
    crypto.getRandomValues(bytes);
    const pwd = Array.from(bytes).map(b => chars[b % chars.length]).join('');
    setInvitePassword(pwd);
  };

  return (
    <div style={styles.page}>
      {/* ── Nav ── */}
      <div style={styles.nav}>
        <div style={styles.navLeft}>
          <button style={styles.backBtn} onClick={onBack}>← Projects</button>
          <div style={styles.navDivider} />
          <div style={styles.navTitle}>Team Management</div>
        </div>
        <div style={styles.navRight}>
          <div style={styles.navUser}>{profile?.full_name || session.user.email}</div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={styles.body}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.eyebrow}>Team Settings</div>
            <h1 style={styles.pageTitle}>{teamName}</h1>
            <div style={styles.teamId}>Team ID: {teamId}</div>
          </div>
          {isAdmin && (
            <button style={styles.addBtn} onClick={() => { setShowInvite(true); setError(''); setSuccess(''); }}>
              + Add Team Member
            </button>
          )}
        </div>

        {/* Success message */}
        {success && (
          <div style={styles.successBox}>{success}</div>
        )}

        {/* ── Add member form ── */}
        {showInvite && isAdmin && (
          <div style={styles.inviteCard}>
            <div style={styles.inviteTitle}>Add New Team Member</div>
            <form onSubmit={handleInvite} style={styles.inviteForm}>
              <div style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Full Name</label>
                  <input
                    style={styles.input}
                    value={inviteName}
                    onChange={e => setInviteName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    autoFocus
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    style={styles.input}
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="jane@example.com"
                    required
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Temporary Password</label>
                  <div style={styles.passwordRow}>
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      value={invitePassword}
                      onChange={e => setInvitePassword(e.target.value)}
                      placeholder="Set a temporary password"
                      required
                    />
                    <button
                      type="button"
                      style={styles.generateBtn}
                      onClick={generatePassword}
                    >
                      Generate
                    </button>
                  </div>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Role</label>
                  <select
                    style={styles.input}
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                  >
                    <option value="member">Member — can create and score projects</option>
                    <option value="manager">Manager — can approve sampling plans</option>
                    <option value="admin">Admin — can also manage team members</option>
                    {profile?.role === 'super_admin' && (
                      <option value="auditor">Auditor — platform-wide read access</option>
                    )}
                  </select>
                </div>
              </div>
              {error && <div style={styles.errorBox}>{error}</div>}
              <div style={styles.formButtons}>
                <button type="submit" style={styles.createBtn} disabled={inviting}>
                  {inviting ? 'Adding…' : 'Add to Team →'}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => { setShowInvite(false); setError(''); }}
                >
                  Cancel
                </button>
              </div>
              <div style={styles.inviteNote}>
                The user can log in immediately at app.thelab.tax with these credentials.
                Share the temporary password with them securely — they should change it after first login.
              </div>
            </form>
          </div>
        )}

        {/* ── Members list ── */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            Team Members
            <span style={styles.memberCount}>{members.length} member{members.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div style={styles.loading}>Loading members…</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Name', 'Email', 'Role', 'Member Since', isAdmin ? 'Actions' : ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map(member => {
                  const rc = ROLE_CONFIG[member.role] || ROLE_CONFIG.member;
                  const isCurrentUser = member.id === currentUserId;
                  return (
                    <tr key={member.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={styles.memberName}>
                          {member.full_name || '—'}
                          {isCurrentUser && <span style={styles.youBadge}>You</span>}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.email}>{member.email}</span>
                      </td>
                      <td style={styles.td}>
                        {isAdmin && !isCurrentUser ? (
                          <select
                            style={{
                              ...styles.roleSelect,
                              color: rc.color,
                              background: rc.bg,
                              borderColor: rc.color,
                            }}
                            value={member.role}
                            onChange={e => handleRoleChange(member.id, e.target.value)}
                          >
                            <option value="member">Member</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                            {profile?.role === 'super_admin' && (
                              <option value="auditor">Auditor</option>
                            )}
                          </select>
                        ) : (
                          <span style={{ ...styles.roleBadge, color: rc.color, background: rc.bg }}>
                            {rc.label}
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.date}>
                          {member.created_at
                            ? new Date(member.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={styles.td}>
                          {!isCurrentUser && (
                            <button
                              style={styles.removeBtn}
                              onClick={() => handleRemove(member.id, member.full_name)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Role legend */}
        <div style={styles.legend}>
          <div style={styles.legendTitle}>Role Permissions</div>
          <div style={{...styles.legendGrid, gridTemplateColumns:'1fr 1fr 1fr'}}>
            <div style={styles.legendCard}>
              <div style={styles.legendRole}>Member</div>
              <div style={styles.legendDesc}>Can create projects, upload populations, enter scores, and download reports. Cannot approve sample plans, manage team members, or see billing.</div>
            </div>
            <div style={styles.legendCard}>
              <div style={{ ...styles.legendRole, color: '#1a5080' }}>Manager</div>
              <div style={styles.legendDesc}>All member permissions plus: approve or reject sampling plans before a sample can be drawn. Prevents irresponsible credit usage. Cannot manage team membership.</div>
            </div>
            <div style={styles.legendCard}>
              <div style={{ ...styles.legendRole, color: '#c98a00' }}>Admin</div>
              <div style={styles.legendDesc}>All manager permissions plus: add and remove team members, change roles, and view credit balance. Recommended for firm principals.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f4f6fb',
    fontFamily: "'Montserrat', sans-serif",
  },
  nav: {
    height: 60,
    background: '#0D2461',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn: {
    background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px', cursor: 'pointer', padding: 0,
  },
  navDivider: { width: 1, height: 28, background: 'rgba(255,255,255,0.15)' },
  navTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700, fontSize: '15px', color: 'white',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '14px' },
  navUser: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: 'rgba(255,255,255,0.5)',
  },
  body: { maxWidth: '860px', margin: '0 auto', padding: '40px 24px 80px' },
  headerRow: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', marginBottom: '28px',
  },
  eyebrow: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#F5A800', marginBottom: '4px',
  },
  pageTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800, fontSize: '28px', color: '#0D2461', margin: '0 0 4px',
  },
  teamId: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px', color: '#8a96b0',
  },
  addBtn: {
    padding: '11px 20px', background: '#F5A800', color: '#0D2461',
    border: 'none', borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800, fontSize: '13px', cursor: 'pointer',
  },
  successBox: {
    background: '#e4f2eb', border: '1px solid #1a5c3a',
    borderRadius: '6px', padding: '12px 16px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px', color: '#1a5c3a', marginBottom: '20px',
  },
  inviteCard: {
    background: 'white', border: '1.5px solid #c8d0e0',
    borderRadius: '8px', padding: '28px', marginBottom: '24px',
  },
  inviteTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800, fontSize: '13px',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#0D2461', marginBottom: '20px',
  },
  inviteForm: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#5a6a8a',
  },
  input: {
    padding: '10px 12px', border: '1.5px solid #c8d0e0',
    borderRadius: '6px', fontFamily: "'Montserrat', sans-serif",
    fontSize: '13px', color: '#0D2461', outline: 'none',
    background: 'white', width: '100%',
  },
  passwordRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  generateBtn: {
    padding: '10px 14px', background: '#f4f6fb',
    border: '1.5px solid #c8d0e0', borderRadius: '6px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: '#5a6a8a', cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorBox: {
    background: '#fff0ee', border: '1px solid #e05a3a',
    borderRadius: '6px', padding: '10px 14px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px', color: '#c03a1a',
  },
  formButtons: { display: 'flex', gap: '12px' },
  createBtn: {
    padding: '11px 22px', background: '#0D2461', color: '#F5A800',
    border: 'none', borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800, fontSize: '13px', cursor: 'pointer',
  },
  cancelBtn: {
    padding: '11px 22px', background: '#f4f6fb', color: '#5a6a8a',
    border: '1.5px solid #c8d0e0', borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600, fontSize: '13px', cursor: 'pointer',
  },
  inviteNote: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: '#8a96b0', lineHeight: 1.6,
  },
  card: {
    background: 'white', border: '1.5px solid #c8d0e0',
    borderRadius: '8px', padding: '28px', marginBottom: '24px',
  },
  cardTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800, fontSize: '13px',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#0D2461', marginBottom: '20px',
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  memberCount: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', fontWeight: 400,
    color: '#8a96b0', letterSpacing: '0.02em',
  },
  loading: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '13px', color: '#8a96b0',
    padding: '20px 0', textAlign: 'center',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: '#8a96b0',
    padding: '8px 12px', textAlign: 'left',
    borderBottom: '1.5px solid #e8ecf5',
  },
  tr: { borderBottom: '1px solid #f0f2f8' },
  td: { padding: '12px 12px', verticalAlign: 'middle' },
  memberName: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700, fontSize: '14px', color: '#0D2461',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  youBadge: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px', color: '#8a96b0',
    background: '#f0f2f8', borderRadius: '10px',
    padding: '2px 8px',
  },
  email: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px', color: '#5a6a8a',
  },
  roleBadge: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', fontWeight: 600,
    padding: '3px 10px', borderRadius: '20px',
    display: 'inline-block',
  },
  roleSelect: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', fontWeight: 600,
    padding: '4px 8px', borderRadius: '4px',
    border: '1.5px solid', cursor: 'pointer',
    outline: 'none',
  },
  date: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: '#8a96b0',
  },
  removeBtn: {
    background: 'none', border: '1px solid #e0d0d0',
    borderRadius: '4px', padding: '4px 10px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: '#c05a5a', cursor: 'pointer',
  },
  legend: { marginTop: '8px' },
  legendTitle: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: '#8a96b0',
    marginBottom: '12px',
  },
  legendGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  legendCard: {
    background: 'white', border: '1.5px solid #e8ecf5',
    borderRadius: '6px', padding: '16px 20px',
  },
  legendRole: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700, fontSize: '13px',
    color: '#1a5c3a', marginBottom: '6px',
  },
  legendDesc: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px', color: '#8a96b0', lineHeight: 1.6,
  },
};
